import axios from 'axios';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, Plus, Trash2, Check, ChevronRight, AlertCircle, Send, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_MESURE_LABELS = {
  pi2: 'pi²', lineaire: 'pi.lin.', heures: 'h', unite: '', fixe: null,
};

const fmt = (v) => (v ?? 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });

// Calcule les 3 prix pour une ligne donnée
function calculerPrix(regle, mesure) {
  const m = parseFloat(mesure) || 0;
  if (regle.type_mesure === 'fixe') {
    return {
      tres_bon: regle.prix_tres_bon,
      bon: regle.prix_bon,
      correct: regle.prix_correct,
    };
  }
  return {
    tres_bon: m * regle.prix_tres_bon + (regle.frais_fixes_tres_bon || 0),
    bon: m * regle.prix_bon + (regle.frais_fixes_bon || 0),
    correct: m * regle.prix_correct + (regle.frais_fixes_correct || 0),
  };
}

export function OngletCalculatrice({ dossier }) {
  const { isProprietaire, token } = useAuth();
  const [regles, setRegles] = useState([]);
  const [lignes, setLignes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);
  const lignesRef = useRef(lignes);
  lignesRef.current = lignes;

  // ── Heures budgétées ──────────────────────────────────────────────────────
  const [bonTravail, setBonTravail] = useState(null);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`${API_URL}/api/calculatrice/regles`).then(r => r.data),
      axios.get(`${API_URL}/api/calculatrice/session/${dossier.id}`).then(r => r.data),
      axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${dossier.id}`, { headers }).then(r => r.data).catch(() => []),
    ]).then(([reglesData, sessionData, bonsData]) => {
      setRegles((reglesData.regles || []).filter(r => r.actif));
      setLignes(sessionData.lignes || []);
      // Bon de travail existant
      const bons = Array.isArray(bonsData) ? bonsData : (bonsData.bons_travail || []);
      if (bons.length > 0) {
        setBonTravail(bons[0]);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dossier.id, token]);

  // Auto-save avec debounce 500ms
  const autoSave = useCallback((newLignes) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.put(`${API_URL}/api/calculatrice/session/${dossier.id}`, { lignes: newLignes }, { headers });
        setLastSaved(new Date());
      } catch { /* silencieux */ }
      finally { setSaving(false); }
    }, 500);
  }, [dossier.id, token]);

  const updateLignes = (newLignes) => {
    setLignes(newLignes);
    autoSave(newLignes);
  };

  const addLigne = (regle_id) => {
    const newLigne = {
      id: `ligne_${Date.now()}`,
      regle_id,
      mesure: '',
      tier_selectionne: 'bon',
    };
    updateLignes([...lignes, newLigne]);
  };

  const updateLigne = (id, changes) => {
    const newLignes = lignes.map(l => l.id === id ? { ...l, ...changes } : l);
    updateLignes(newLignes);
  };

  const removeLigne = (id) => {
    updateLignes(lignes.filter(l => l.id !== id));
  };

  // Totaux par tier
  const totaux = lignes.reduce((acc, ligne) => {
    const regle = regles.find(r => r.id === ligne.regle_id);
    if (!regle) return acc;
    const prix = calculerPrix(regle, ligne.mesure);
    return {
      tres_bon: acc.tres_bon + (prix.tres_bon || 0),
      bon: acc.bon + (prix.bon || 0),
      correct: acc.correct + (prix.correct || 0),
    };
  }, { tres_bon: 0, bon: 0, correct: 0 });

  const reglesParCategorie = regles.reduce((acc, r) => {
    acc[r.nom] = r;
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  if (regles.length === 0) return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="pt-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold text-amber-900">Aucune règle de calcul configurée</p>
          <p className="text-sm text-amber-700 mt-1">
            Allez dans <strong>Configuration → Règles de calcul</strong> pour créer vos premières catégories de travaux.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {/* Header avec statut sauvegarde */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-600" />
          <span className="font-semibold text-slate-700">Calculatrice — {dossier.nom_dossier}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          {saving ? (
            <><RefreshCw className="w-3 h-3 animate-spin" /> Sauvegarde...</>
          ) : lastSaved ? (
            <><Check className="w-3 h-3 text-emerald-500" /> Sauvegardé {lastSaved.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</>
          ) : null}
        </div>
      </div>

      {/* Sélecteur d'ajout de ligne */}
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="pt-4 pb-3">
          <p className="text-sm font-medium text-emerald-800 mb-2">+ Ajouter une catégorie de travaux</p>
          <div className="flex flex-wrap gap-2">
            {regles.map(regle => (
              <button
                key={regle.id}
                onClick={() => addLigne(regle.id)}
                className="px-3 py-1.5 text-sm bg-white border border-emerald-300 text-emerald-700 rounded-full hover:bg-emerald-100 hover:border-emerald-500 transition-all flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> {regle.nom}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lignes de calcul */}
      {lignes.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <Calculator className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Ajoutez une catégorie ci-dessus pour commencer le calcul</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lignes.map((ligne, idx) => {
            const regle = regles.find(r => r.id === ligne.regle_id);
            if (!regle) return null;
            const prix = calculerPrix(regle, ligne.mesure);
            const unite = regle.unite_label || TYPE_MESURE_LABELS[regle.type_mesure];

            return (
              <Card key={ligne.id} className="border-slate-200">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start gap-3">
                    {/* Numéro + Nom */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                          <span className="font-semibold text-slate-800">{regle.nom}</span>
                          <Badge variant="outline" className="text-xs">{TYPE_MESURE_LABELS[regle.type_mesure] === null ? 'Prix fixe' : regle.type_mesure}</Badge>
                        </div>
                        <button onClick={() => removeLigne(ligne.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Mesure input */}
                      {regle.type_mesure !== 'fixe' && (
                        <div className="flex items-center gap-2 max-w-xs">
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            placeholder={`Entrez la mesure (${unite})`}
                            value={ligne.mesure}
                            onChange={e => updateLigne(ligne.id, { mesure: e.target.value })}
                            className="max-w-[180px]"
                          />
                          <span className="text-sm text-slate-500 font-medium">{unite}</span>
                        </div>
                      )}

                      {/* 3 prix */}
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { key: 'tres_bon', label: '⭐⭐⭐ Très bon', bgSel: 'bg-emerald-600 text-white border-emerald-600', bgDef: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100' },
                          { key: 'bon', label: '⭐⭐ Bon', bgSel: 'bg-blue-600 text-white border-blue-600', bgDef: 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100' },
                          { key: 'correct', label: '⭐ Correct', bgSel: 'bg-amber-500 text-white border-amber-500', bgDef: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100' },
                        ].map(tier => {
                          const isSelected = ligne.tier_selectionne === tier.key;
                          const montant = prix[tier.key] || 0;
                          const isReady = regle.type_mesure === 'fixe' || (parseFloat(ligne.mesure) > 0);
                          return (
                            <button
                              key={tier.key}
                              onClick={() => updateLigne(ligne.id, { tier_selectionne: tier.key })}
                              className={`p-3 rounded-lg border-2 transition-all text-left ${isSelected ? tier.bgSel : tier.bgDef}`}
                            >
                              <p className="text-xs font-semibold mb-1">{tier.label}</p>
                              <p className={`text-xl font-bold ${isReady ? '' : 'opacity-30'}`}>
                                {isReady ? fmt(montant) : '—'}
                              </p>
                              {isSelected && <div className="flex items-center gap-1 mt-1 text-xs opacity-80"><Check className="w-3 h-3" /> Sélectionné</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Totaux */}
          <Card className="border-slate-800 bg-slate-900 text-white">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-lg">Total estimé</span>
                <span className="text-xs text-slate-400">{lignes.length} catégorie{lignes.length > 1 ? 's' : ''}</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'tres_bon', label: '⭐⭐⭐ Très bon', color: 'text-emerald-400' },
                  { key: 'bon', label: '⭐⭐ Bon', color: 'text-blue-400' },
                  { key: 'correct', label: '⭐ Correct', color: 'text-amber-400' },
                ].map(tier => (
                  <div key={tier.key} className="text-center">
                    <p className="text-xs text-slate-400 mb-1">{tier.label}</p>
                    <p className={`text-2xl font-bold ${tier.color}`}>{fmt(totaux[tier.key])}</p>
                  </div>
                ))}
              </div>

              {/* Bouton créer soumission (optionnel) */}
              <div className="mt-4 pt-3 border-t border-slate-700 flex justify-end">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                  onClick={() => toast.info('Sélectionnez un tier dans les lignes, puis utilisez l\'onglet Soumissions pour créer votre soumission.')}
                >
                  <Send className="w-3.5 h-3.5" /> Utiliser ce prix en soumission
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* ── Section Calcul des heures budgétées ───────────────────────── */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="flex items-center gap-2 text-base text-blue-900">
            <Clock className="w-5 h-5" />
            Heures budgétées
            {bonTravail?.heures_budgetees > 0 && (
              <Badge className="bg-blue-600 text-white ml-2">{bonTravail.heures_budgetees}h actuelles</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-start gap-3 p-3 bg-white border border-blue-200 rounded-lg">
            <Check className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-800">Calcul automatique</p>
              <p className="text-xs text-slate-500 mt-1">
                Les heures budgétées sont maintenant calculées automatiquement dans le <strong>bon de travail</strong> dès
                qu'une équipe est assignée. La formule utilise le coût total de l'équipe par heure et l'objectif de 45% du budget main d'oeuvre.
              </p>
              {bonTravail?.heures_budgetees > 0 && (
                <p className="text-sm font-bold text-blue-700 mt-2">{bonTravail.heures_budgetees}h budgétées pour ce bon de travail</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OngletCalculatrice;
